/**
 * ToolCallGroup wraps consecutive tool-call parts in a collapsible
 * disclosure, matching V1's CollapsedToolCalls pattern.
 *
 * Used as the `ToolGroup` component in MessagePrimitive.Parts.
 * Assistant-UI automatically groups consecutive tool-call parts and
 * passes them through this wrapper.
 *
 * Single tool calls pass through without the collapsible wrapper.
 * When the group contains a `final_result`, children render without
 * collapsing so the main response + artifacts remain visible.
 */
import { useEffect, useState, type FC, type PropsWithChildren } from "react";
import { useAuiState } from "@assistant-ui/react";
import { ChevronRight } from "lucide-react";
import { cn } from "~/lib/utils";

export const ToolCallGroup: FC<PropsWithChildren<{ startIndex: number; endIndex: number }>> = ({
  children,
  startIndex,
  endIndex,
}) => {
  const message = useAuiState((s) => s.message);
  const isRunning = useAuiState((s) => s.message.status?.type === "running");
  const count = endIndex - startIndex + 1;

  const hasFinalResult = message.content?.some(
    (part: { type: string; toolName?: string }, idx: number) =>
      idx >= startIndex &&
      idx <= endIndex &&
      part.type === "tool-call" &&
      part.toolName === "final_result"
  );

  const [isExpanded, setIsExpanded] = useState(isRunning);

  // Auto-collapse when run finishes
  useEffect(() => {
    if (!isRunning) {
      setIsExpanded(false);
    }
  }, [isRunning]);

  if (count <= 1 || hasFinalResult) {
    return <>{children}</>;
  }

  return (
    <div className="px-2 pt-2">
      <details
        open={isExpanded}
        onToggle={(e) => {
          const target = e.target as HTMLDetailsElement;
          setIsExpanded(target.open);
          e.stopPropagation();
        }}
      >
        <summary className="list-none cursor-pointer select-none w-fit">
          <div className="flex items-center">
            <span className="text-xs font-medium text-purple-600 px-1">{count} tool calls</span>
            <ChevronRight
              className={cn(
                "size-4 text-purple-600 transition-transform duration-200",
                isExpanded && "rotate-90"
              )}
            />
          </div>
        </summary>
        <div className="mt-3 space-y-2">{children}</div>
      </details>
    </div>
  );
};
